import React, { useState } from 'react';
import { useDSLState } from './hooks/useDSLState';
import { SectionCard } from './components/SectionCard';
import DSLTester from './components/DSLTester';
import {
  AddSectionModal,
  AddNestedSectionModal,
  LLMActionModal,
  EditSectionConfigModal,
  ConfirmationModal
} from './components/Modals';
import {
  Container,
  SectionCard as StyledSectionCard,
  SectionHeader,
  SectionTitle,
  CollapseIcon,
  SectionBody,
  Chip,
  FloatingButton,
  ToastContainer,
  Toast,
  JSONOutput,
  ButtonGroup,
  Button,
  SecondaryButton
} from './components/StyledComponents';

const DSLCreator = () => {
  const [showTester, setShowTester] = useState(false);
  const [showJsonImporter, setShowJsonImporter] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [pendingRemoveLLMAction, setPendingRemoveLLMAction] = useState(null); // { sectionName, actionId, parentSectionName }
  
  const {
    // State
    config,
    showAddSection,
    showAddNestedSection,
    showAddLLMActionModal,
    showEditLLMActionModal,
    showEditSectionConfigModal,
    showRemoveSectionModal,
    showRemoveLLMActionModal,
    newSectionType,
    newSectionName,
    newNestedSectionType,
    newNestedSectionName,
    parentSectionName,
    selectedSectionName,
    selectedActionId,
    actionName,
    actionPrompt,
    actionFeedbackType,
    actionEnabled,
    actionFormatAsJson,
    llmActionsToEdit,
    selectedSectionConfigField,
    selectedSectionConfigValue,
    sectionToRemove,
    llmActionsToRemove,
    openSections,
    showToast,
    toastMessage,
    toastType,
    isLoading,

    // Setters
    setShowAddSection,
    setShowAddNestedSection,
    setShowAddLLMActionModal,
    setShowEditLLMActionModal,
    setShowEditSectionConfigModal,
    setShowRemoveSectionModal,
    setShowRemoveLLMActionModal,
    setNewSectionType,
    setNewSectionName,
    setNewNestedSectionType,
    setNewNestedSectionName,
    setParentSectionName,
    setSelectedSectionName,
    setSelectedActionId,
    setActionName,
    setActionPrompt,
    setActionFeedbackType,
    setActionEnabled,
    setActionFormatAsJson,
    setLLMActionsToEdit,
    setSelectedSectionConfigField,
    setSelectedSectionConfigValue,
    setSectionToRemove,
    setLLMActionsToRemove,
    setOpenSections,
    setIncludeDataField,
    setShowToast,
    setToastMessage,
    setToastType,
    setIsLoading,

    // Functions
    addSection,
    removeSection,
    updateSectionConfig,
    addLLMAction,
    removeLLMAction,
    updateLLMAction,
    toggleIncludeData,
    toggleSection,
    isSectionOpen,
    showToastMessage,
    generateJSON,
    loadFromJSON,
    validateSectionName,
    validateNestedSectionName,
    validateLLMActionName
  } = useDSLState();

  // Event handlers
  const handleAddSection = () => {
    if (newSectionName && newSectionType) {
      const error = validateSectionName(newSectionName);
      if (error) {
        showToastMessage(error, 'error');
        return;
      }
      
      addSection(newSectionType, newSectionName);
      setShowAddSection(false);
      setNewSectionType('');
      setNewSectionName('');
      showToastMessage('Section added successfully!', 'success');
    } else {
      showToastMessage('Please enter a section name and select a type.', 'error');
    }
  };

  const handleAddNestedSection = () => {
    if (newNestedSectionName && newNestedSectionType) {
      const error = validateNestedSectionName(newNestedSectionName, parentSectionName);
      if (error) {
        showToastMessage(error, 'error');
        return;
      }
      
      addSection(newNestedSectionType, newNestedSectionName, parentSectionName);
      setShowAddNestedSection(false);
      setNewNestedSectionType('');
      setNewNestedSectionName('');
      showToastMessage('Nested section added successfully!', 'success');
    } else {
      showToastMessage('Please enter a nested section name and select a type.', 'error');
    }
  };

  const handleAddLLMAction = () => {
    if (actionName && actionPrompt) {
      if (selectedSectionName) {
        // Create the LLM action with the form data
        const newAction = {
          id: Date.now(),
          name: actionName,
          prompt: actionPrompt,
          feedbackType: actionFeedbackType,
          enabled: actionEnabled,
          formatAsJson: actionFormatAsJson
        };
        
        // Add the action to the section using the hook function
        addLLMAction(selectedSectionName, null, newAction);
        
        setShowAddLLMActionModal(false);
        setActionName('');
        setActionPrompt('');
        setActionFeedbackType('current');
        setActionEnabled(true);
        setActionFormatAsJson(false);
        showToastMessage('LLM action added successfully!', 'success');
      } else {
        showToastMessage('Please select a section to add the LLM action to.', 'error');
      }
    } else {
      showToastMessage('Please enter an action name and prompt.', 'error');
    }
  };

  const handleRemoveSection = () => {
    if (sectionToRemove) {
      removeSection(sectionToRemove);
      setShowRemoveSectionModal(false);
      setSectionToRemove('');
      showToastMessage('Section removed successfully!', 'success');
    }
  };

  const handleRemoveLLMAction = () => {
    if (pendingRemoveLLMAction) {
      const { sectionName, actionId, parentSectionName } = pendingRemoveLLMAction;
      removeLLMAction(sectionName, actionId, parentSectionName || null);
      setPendingRemoveLLMAction(null);
      setShowRemoveLLMActionModal(false);
      showToastMessage('LLM action removed successfully!', 'success');
    } else if (llmActionsToRemove.length > 0) {
      // Fallback for legacy path
      llmActionsToRemove.forEach(actionId => {
        removeLLMAction(selectedSectionName, actionId);
      });
      setLLMActionsToRemove([]);
      setShowRemoveLLMActionModal(false);
      showToastMessage('LLM actions removed successfully!', 'success');
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generateJSON());
    showToastMessage('JSON copied to clipboard!', 'success');
  };

  const handleDownloadJSON = () => {
    const blob = new Blob([generateJSON()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dsl-config.json';
    a.click();
    URL.revokeObjectURL(url);
    showToastMessage('JSON downloaded successfully!', 'success');
  };

  return (
    <Container>
      <h1>DSL Framework Creator</h1>
      
      {/* Data Inclusion Settings */}
      <StyledSectionCard>
        <SectionHeader onClick={() => toggleSection('includeData')}>
          <SectionTitle>Data Inclusion Settings</SectionTitle>
          <CollapseIcon open={isSectionOpen('includeData')}>▼</CollapseIcon>
        </SectionHeader>
        <SectionBody>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(config.includeData)
              .filter(([_, value]) => typeof value === 'boolean')
              .map(([field, enabled]) => (
              <Chip
                key={field}
                onClick={() => toggleIncludeData(field)}
                style={{ 
                  cursor: 'pointer', 
                  background: enabled ? '#e0f2fe' : '#f1f5f9', 
                  color: enabled ? '#1d4ed8' : '#64748b' 
                }}
              >
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </Chip>
            ))}
          </div>

          {config.includeData.score_distribution && (
            <div style={{ marginTop: '12px' }}>
              <h4>Score Distribution Bands</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Low</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.low?.[0] ?? 0}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, low: [val, config.includeData.score_distribution_bands.low?.[1] ?? 4] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.low?.[1] ?? 4}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, low: [config.includeData.score_distribution_bands.low?.[0] ?? 0, val] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Neutral</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.neutral?.[0] ?? 5}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, neutral: [val, config.includeData.score_distribution_bands.neutral?.[1] ?? 6] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.neutral?.[1] ?? 6}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, neutral: [config.includeData.score_distribution_bands.neutral?.[0] ?? 5, val] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>High</div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.high?.[0] ?? 7}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, high: [val, config.includeData.score_distribution_bands.high?.[1] ?? 10] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                    <span>to</span>
                    <input
                      type="number"
                      value={config.includeData.score_distribution_bands.high?.[1] ?? 10}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        const bands = { ...config.includeData.score_distribution_bands, high: [config.includeData.score_distribution_bands.high?.[0] ?? 7, val] };
                        setIncludeDataField('score_distribution_bands', bands);
                      }}
                      style={{ width: '70px' }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </SectionBody>
      </StyledSectionCard>

      {/* Sections */}
      {Object.entries(config.sections).map(([sectionName, section]) => (
        <SectionCard
          key={section.id}
          sectionName={sectionName}
          section={section}
          isOpen={isSectionOpen(sectionName)}
          onToggle={() => toggleSection(sectionName)}
          onRemove={(name, parent) => {
            setSectionToRemove(name);
            setShowRemoveSectionModal(true);
          }}
          onAddLLMAction={(name, parent) => {
            setSelectedSectionName(name);
            setShowAddLLMActionModal(true);
          }}
          onUpdateConfig={updateSectionConfig}
          onUpdateLLMAction={updateLLMAction}
          onRemoveLLMAction={(name, actionId, parent) => {
            setPendingRemoveLLMAction({ sectionName: name, actionId, parentSectionName: parent || null });
            setShowRemoveLLMActionModal(true);
          }}
          showToastMessage={showToastMessage}
          validateLLMActionName={validateLLMActionName}
        />
      ))}

      {/* JSON Output */}
      <StyledSectionCard>
        <SectionHeader onClick={() => toggleSection('jsonOutput')}>
          <SectionTitle>Generated JSON Configuration</SectionTitle>
          <CollapseIcon open={isSectionOpen('jsonOutput')}>▼</CollapseIcon>
        </SectionHeader>
        <SectionBody>
          <JSONOutput>{generateJSON()}</JSONOutput>
          <ButtonGroup>
            <Button onClick={handleCopyToClipboard}>
              Copy to Clipboard
            </Button>
            <SecondaryButton onClick={handleDownloadJSON}>
              Download JSON
            </SecondaryButton>
            <SecondaryButton onClick={() => setShowJsonImporter(!showJsonImporter)}>
              {showJsonImporter ? 'Hide Importer' : 'Load JSON'}
            </SecondaryButton>
            <Button 
              onClick={() => setShowTester(!showTester)}
              style={{ background: '#10b981' }}
            >
              {showTester ? 'Hide Tester' : 'Test DSL Configuration'}
            </Button>
          </ButtonGroup>

          {showJsonImporter && (
            <div style={{ marginTop: '12px' }}>
              <h4>Paste JSON Configuration</h4>
              <textarea
                value={importJsonText}
                onChange={(e) => setImportJsonText(e.target.value)}
                placeholder="Paste your JSON configuration here"
                style={{ width: '100%', minHeight: '160px' }}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Button onClick={() => { loadFromJSON(importJsonText); }}>
                  Load Configuration
                </Button>
                <SecondaryButton onClick={() => setImportJsonText('')}>
                  Clear
                </SecondaryButton>
              </div>
            </div>
          )}
        </SectionBody>
      </StyledSectionCard>

      {/* DSL Tester */}
      {showTester && (
        <DSLTester dslConfig={JSON.parse(generateJSON())} />
      )}

      {/* Toast */}
      {showToast && (
        <ToastContainer>
          <Toast type={toastType}>
            <span>{toastType.toUpperCase()}</span>
            <span>{toastMessage}</span>
          </Toast>
        </ToastContainer>
      )}

      {/* Modals */}
      <AddSectionModal
        isOpen={showAddSection}
        onClose={() => {
          setShowAddSection(false);
          setNewSectionType('');
          setNewSectionName('');
        }}
        sectionName={newSectionName}
        setSectionName={setNewSectionName}
        sectionType={newSectionType}
        setSectionType={setNewSectionType}
        onAdd={handleAddSection}
        isLoading={isLoading}
      />

      <AddNestedSectionModal
        isOpen={showAddNestedSection}
        onClose={() => {
          setShowAddNestedSection(false);
          setNewNestedSectionType('');
          setNewNestedSectionName('');
        }}
        sectionName={newNestedSectionName}
        setSectionName={setNewNestedSectionName}
        sectionType={newNestedSectionType}
        setSectionType={setNewNestedSectionType}
        onAdd={handleAddNestedSection}
        isLoading={isLoading}
      />

      <LLMActionModal
        isOpen={showAddLLMActionModal}
        onClose={() => {
          setShowAddLLMActionModal(false);
          setActionName('');
          setActionPrompt('');
          setActionFeedbackType('current');
          setActionEnabled(true);
          setActionFormatAsJson(false);
        }}
        isEditing={false}
        actionName={actionName}
        setActionName={setActionName}
        actionPrompt={actionPrompt}
        setActionPrompt={setActionPrompt}
        feedbackType={actionFeedbackType}
        setFeedbackType={setActionFeedbackType}
        enabled={actionEnabled}
        setEnabled={setActionEnabled}
        formatAsJson={actionFormatAsJson}
        setFormatAsJson={setActionFormatAsJson}
        onSave={handleAddLLMAction}
        isLoading={isLoading}
        sectionType={config.sections?.[selectedSectionName]?.type || ''}
      />

      <ConfirmationModal
        isOpen={showRemoveSectionModal}
        onClose={() => {
          setShowRemoveSectionModal(false);
          setSectionToRemove('');
        }}
        title="Confirm Removal"
        message={`Are you sure you want to remove the section "${sectionToRemove}"? This action cannot be undone.`}
        onConfirm={handleRemoveSection}
        confirmText="Remove Section"
        isLoading={isLoading}
      />

      <ConfirmationModal
        isOpen={showRemoveLLMActionModal}
        onClose={() => {
          setShowRemoveLLMActionModal(false);
          setLLMActionsToRemove([]);
          setPendingRemoveLLMAction(null);
        }}
        title="Confirm Removal"
        message="Are you sure you want to remove the selected LLM action? This action cannot be undone."
        onConfirm={handleRemoveLLMAction}
        confirmText="Remove LLM Action"
        isLoading={isLoading}
      />

      {/* Floating Add Button */}
      <FloatingButton onClick={() => setShowAddSection(true)}>+</FloatingButton>
    </Container>
  );
};

export default DSLCreator;
